// app/javascript/controllers/data_table_builder_controller.js
// Spreadsheet-style builder for user-defined "Data Tables" read by Power BI.
// Serializes to { columns: [{ name, type }], rows: [[cell, ...], ...] } into a
// hidden field the DataTablesController explodes into relational rows.
import { Controller } from "stimulus"

const DATA_TYPES = ["text", "number", "date"]
const MAX_PASTE_ROWS = 500

export default class extends Controller {
  static targets = ["table", "dataInput"]

  connect() {
    let initialData = {}
    const initialAttr = this.element.dataset.initialData
    if (initialAttr) {
      try {
        initialData = JSON.parse(initialAttr)
      } catch (e) {
        console.error("Error parsing initial data:", e)
      }
    }

    if (initialData && Array.isArray(initialData.columns) && initialData.columns.length > 0) {
      this.tableData = {
        columns: initialData.columns.map(col => ({
          name: col.name || "",
          type: DATA_TYPES.includes(col.type) ? col.type : "text"
        })),
        rows: Array.isArray(initialData.rows) ? initialData.rows : []
      }
    } else {
      this.tableData = {
        columns: [{ name: "Column 1", type: "text" }],
        rows: [[""]]
      }
    }

    this.render()
    this.updateHiddenInput()
  }

  addColumn() {
    const columnNumber = this.tableData.columns.length + 1
    this.tableData.columns.push({ name: `Column ${columnNumber}`, type: "text" })
    this.tableData.rows.forEach(row => row.push(""))
    this.render()
    this.updateHiddenInput()
    this.scrollToRight() // keep the new column and the + button in view
  }

  addRow() {
    const newRow = new Array(this.tableData.columns.length).fill("")
    this.tableData.rows.push(newRow)
    this.render()
    this.updateHiddenInput()
  }

  removeColumn(event) {
    const columnIndex = parseInt(event.target.dataset.columnIndex)
    if (this.tableData.columns.length <= 1) {
      alert("Table must have at least one column")
      return
    }
    this.tableData.columns.splice(columnIndex, 1)
    this.tableData.rows.forEach(row => row.splice(columnIndex, 1))
    this.render()
    this.updateHiddenInput()
  }

  removeRow(event) {
    const rowIndex = parseInt(event.target.dataset.rowIndex)
    if (this.tableData.rows.length <= 1) {
      alert("Table must have at least one row")
      return
    }
    this.tableData.rows.splice(rowIndex, 1)
    this.render()
    this.updateHiddenInput()
  }

  updateHeader(event) {
    const columnIndex = parseInt(event.target.dataset.columnIndex)
    this.tableData.columns[columnIndex].name = event.target.value
    this.updateHiddenInput()
  }

  updateColumnType(event) {
    const columnIndex = parseInt(event.target.dataset.columnIndex)
    this.tableData.columns[columnIndex].type = event.target.value
    // Re-render so cell inputs pick up the new input type (e.g. native date picker).
    this.render()
    this.updateHiddenInput()
  }

  updateCell(event) {
    const rowIndex = parseInt(event.target.dataset.rowIndex)
    const columnIndex = parseInt(event.target.dataset.columnIndex)
    this.tableData.rows[rowIndex][columnIndex] = event.target.value
    this.updateHiddenInput()
  }

  // --- Paste-from-Excel support ---------------------------------------------
  // Excel/Sheets put copied ranges on the clipboard as tab-separated text.
  // Pasting a range into a cell fills down-right from that cell (Excel anchor
  // semantics), auto-adding rows and columns as needed. Single values fall
  // through to the browser's normal paste.

  pasteCells(event) {
    const text = this.clipboardText(event)
    if (!this.isMultiCell(text)) return

    event.preventDefault()
    const grid = this.parseClipboard(text)
    this.writeGrid(
      grid,
      parseInt(event.target.dataset.rowIndex),
      parseInt(event.target.dataset.columnIndex)
    )
    this.render()
    this.updateHiddenInput()
  }

  // Pasting a range into a column header treats the first clipboard row as
  // header names and any remaining rows as data starting at row 0 — so a
  // whole table (headers included) can be pasted into the first header cell.
  pasteHeaders(event) {
    const text = this.clipboardText(event)
    if (!this.isMultiCell(text)) return

    event.preventDefault()
    const grid = this.parseClipboard(text)
    const startCol = parseInt(event.target.dataset.columnIndex)

    const headers = grid.shift() || []
    headers.forEach((name, i) => {
      this.ensureColumn(startCol + i)
      if (name.trim() !== "") this.tableData.columns[startCol + i].name = name.trim()
    })

    if (grid.length > 0) this.writeGrid(grid, 0, startCol)
    this.render()
    this.updateHiddenInput()
  }

  clipboardText(event) {
    return event.clipboardData ? event.clipboardData.getData("text/plain") : ""
  }

  isMultiCell(text) {
    const trimmed = text.trim()
    return trimmed.includes("\t") || /[\r\n]/.test(trimmed)
  }

  parseClipboard(text) {
    const lines = text.replace(/\r\n?/g, "\n").split("\n")
    while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop()

    if (lines.length > MAX_PASTE_ROWS) {
      alert(`Paste is limited to ${MAX_PASTE_ROWS} rows at a time; the first ${MAX_PASTE_ROWS} were used.`)
      lines.length = MAX_PASTE_ROWS
    }

    return lines.map(line => line.split("\t"))
  }

  ensureColumn(index) {
    while (this.tableData.columns.length <= index) {
      this.tableData.columns.push({ name: `Column ${this.tableData.columns.length + 1}`, type: "text" })
      this.tableData.rows.forEach(row => row.push(""))
    }
  }

  ensureRow(index) {
    while (this.tableData.rows.length <= index) {
      this.tableData.rows.push(new Array(this.tableData.columns.length).fill(""))
    }
  }

  writeGrid(grid, startRow, startCol) {
    grid.forEach((cells, r) => {
      this.ensureRow(startRow + r)
      cells.forEach((value, c) => {
        this.ensureColumn(startCol + c)
        this.tableData.rows[startRow + r][startCol + c] = value
      })
    })
  }

  cellInputType(columnType) {
    if (columnType === "number") return "number"
    if (columnType === "date") return "date"
    return "text"
  }

  typeOptions(selectedType) {
    return DATA_TYPES.map(type => {
      const label = type.charAt(0).toUpperCase() + type.slice(1)
      const selected = type === selectedType ? "selected" : ""
      return `<option value="${type}" ${selected}>${label}</option>`
    }).join("")
  }

  render() {
    if (!this.hasTableTarget) {
      console.error("Cannot render table - target not found")
      return
    }

    // innerHTML replacement destroys the scroll container; remember its
    // position so structural edits don't snap a wide table back to the left.
    const previousScrollLeft = this.scrollContainer() ? this.scrollContainer().scrollLeft : 0

    const tableHtml = `
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 border border-gray-300">
          <thead class="bg-gray-50">
            <tr class="sortable-header-row">
              ${this.tableData.columns.map((col, colIndex) => `
                <th class="px-3 py-2 border border-gray-300 relative sortable-header" data-column-index="${colIndex}">
                  <div class="flex items-center">
                    <div class="drag-handle cursor-move p-1 text-gray-400 hover:text-gray-600" title="Drag to reorder">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value="${this.escapeHtml(col.name)}"
                      data-column-index="${colIndex}"
                      data-action="input->data-table-builder#updateHeader paste->data-table-builder#pasteHeaders"
                      class="flex-1 border-none bg-transparent font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                    />
                  </div>
                  <select
                    data-column-index="${colIndex}"
                    data-action="change->data-table-builder#updateColumnType"
                    class="mt-1 block w-full text-xs text-gray-600 border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >${this.typeOptions(col.type)}</select>
                  <button
                    type="button"
                    data-column-index="${colIndex}"
                    data-action="click->data-table-builder#removeColumn"
                    class="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs hover:bg-red-600 focus:outline-none"
                    title="Remove column"
                  >&times;</button>
                </th>
              `).join('')}
              <th class="px-3 py-2 w-12">
                <button
                  type="button"
                  data-action="click->data-table-builder#addColumn"
                  class="bg-blue-500 text-white rounded px-2 py-1 text-xs hover:bg-blue-600 focus:outline-none"
                  title="Add column"
                >+</button>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            ${this.tableData.rows.map((row, rowIndex) => `
              <tr>
                ${row.map((cell, colIndex) => `
                  <td class="px-3 py-2 border border-gray-300">
                    <input
                      type="${this.cellInputType(this.tableData.columns[colIndex].type)}"
                      value="${this.escapeHtml(cell)}"
                      data-row-index="${rowIndex}"
                      data-column-index="${colIndex}"
                      data-action="input->data-table-builder#updateCell paste->data-table-builder#pasteCells"
                      class="w-full border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                    />
                  </td>
                `).join('')}
                <td class="px-3 py-2 w-12">
                  <button
                    type="button"
                    data-row-index="${rowIndex}"
                    data-action="click->data-table-builder#removeRow"
                    class="bg-red-500 text-white rounded px-2 py-1 text-xs hover:bg-red-600 focus:outline-none"
                    title="Remove row"
                  >&times;</button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      <div class="mt-2">
        <button
          type="button"
          data-action="click->data-table-builder#addRow"
          class="bg-blue-500 text-white rounded px-3 py-1 text-sm hover:bg-blue-600 focus:outline-none"
        >Add Row</button>
        <button
          type="button"
          data-action="click->data-table-builder#addColumn"
          class="ml-2 bg-blue-500 text-white rounded px-3 py-1 text-sm hover:bg-blue-600 focus:outline-none"
        >Add Column</button>
      </div>
    `

    this.tableTarget.innerHTML = tableHtml
    if (this.scrollContainer()) this.scrollContainer().scrollLeft = previousScrollLeft
    this.initializeSortableColumns()
  }

  scrollContainer() {
    return this.hasTableTarget ? this.tableTarget.querySelector('.overflow-x-auto') : null
  }

  scrollToRight() {
    const container = this.scrollContainer()
    if (container) container.scrollLeft = container.scrollWidth
  }

  initializeSortableColumns() {
    const headerRow = this.tableTarget.querySelector('.sortable-header-row')
    if (!headerRow) return

    $(headerRow).sortable({
      items: '.sortable-header',
      handle: '.drag-handle',
      axis: 'x',
      cursor: 'move',
      tolerance: 'pointer',
      placeholder: 'sortable-placeholder',
      helper: 'clone',
      start: (event, ui) => {
        ui.placeholder.height(ui.helper.height())
        ui.placeholder.css('background-color', '#e5e7eb')
      },
      stop: () => this.reorderColumns()
    })
  }

  reorderColumns() {
    const headerCells = this.tableTarget.querySelectorAll('.sortable-header')
    const newOrder = Array.from(headerCells).map(cell => parseInt(cell.dataset.columnIndex))

    this.tableData.columns = newOrder.map(oldIndex => this.tableData.columns[oldIndex])
    this.tableData.rows = this.tableData.rows.map(row => newOrder.map(oldIndex => row[oldIndex]))

    this.render()
    this.updateHiddenInput()
  }

  updateHiddenInput() {
    if (this.hasDataInputTarget) {
      this.dataInputTarget.value = JSON.stringify(this.tableData)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text == null ? "" : String(text)
    return div.innerHTML
  }
}
